import React, { useState } from 'react';
import SharedPageLayout from './layout/SharedPageLayout';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  BookOpen, 
  Play, 
  Code2, 
  Workflow,
  TestTube2,
  Zap,
  ArrowRight,
  ArrowLeft,
  Clock,
  CheckCircle2,
  Copy,
  Terminal,
  MousePointer,
  Settings,
  Eye,
  Upload,
  Link2,
  GitBranch,
  Calendar,
  AlertCircle,
  Lightbulb
} from 'lucide-react';

// Guide content data - detailed tutorials for each guide
const guideContent = {
  'quick-start': {
    title: 'Quick Start Guide',
    duration: '5 min',
    level: 'Beginner',
    sections: [
      {
        title: '1. Create Your Account',
        content: `Sign up for a free Lexa.run account to get started. The free trial includes 5 test executions per month - no credit card required.`,
        steps: [
          'Click "Get Started Free" on the homepage',
          'Enter your name, email, and create a password',
          'Choose your organization name and workspace URL',
          'Verify your email address'
        ]
      },
      {
        title: '2. Navigate the Dashboard',
        content: `Once logged in, you'll see the main dashboard with quick access to all testing modules.`,
        steps: [
          'Flow Testing - AI-powered UI test automation',
          'API Testing - REST API testing with data chaining',
          'Sprint QA - Requirements and test case management',
          'Test Suites - Organize and schedule test runs'
        ]
      },
      {
        title: '3. Create Your First Test',
        content: `The fastest way to create a test is using our AI test generator. Simply describe what you want to test in plain English.`,
        steps: [
          'Go to Flow Testing → Tests',
          'Click "Create Test" and select "AI Generate"',
          'Describe your test: e.g., "Login to the app and verify the dashboard loads"',
          'AI will generate complete test steps automatically'
        ]
      },
      {
        title: '4. Run Your Test',
        content: `Execute your test and watch it run in real-time with our live viewer.`,
        steps: [
          'Click the "Run" button on your test',
          'Select browser (Chrome, Firefox, Safari) and device type',
          'Watch the test execute in the live preview',
          'Review results with screenshots and logs'
        ]
      }
    ],
    tips: [
      'Start with simple tests before building complex scenarios',
      'Use descriptive test names for easy identification',
      'Check the Executions tab to view test history'
    ]
  },
  'creating-first-test': {
    title: 'Creating Your First Test',
    duration: '10 min',
    level: 'Beginner',
    sections: [
      {
        title: '1. Understanding Test Types',
        content: `Lexa.run supports multiple ways to create tests depending on your needs.`,
        steps: [
          'AI Generate - Describe tests in plain English, AI creates the steps',
          'Manual Create - Build tests step-by-step with full control',
          'Live Recording - Record your actions in a live browser session',
          'Import - Import existing Playwright/Selenium tests'
        ]
      },
      {
        title: '2. Using AI Test Generation',
        content: `Our AI understands natural language and converts it into executable test steps.`,
        code: `Example prompt:
"Go to https://example.com, click the Login button, 
enter username 'testuser' and password 'test123', 
click Submit, and verify the welcome message appears"`,
        steps: [
          'Be specific about URLs, button names, and expected outcomes',
          'Include wait conditions: "wait for the page to load"',
          'Mention assertions: "verify the text contains..."',
          'Reference element locations: "click the blue Submit button"'
        ]
      },
      {
        title: '3. Editing Generated Tests',
        content: `After AI generates your test, you can fine-tune each step.`,
        steps: [
          'Click "Edit Test" to modify any step',
          'Drag steps to reorder them',
          'Add screenshot steps for visual verification',
          'Update selectors if elements change'
        ]
      },
      {
        title: '4. Running and Debugging',
        content: `Execute your test and analyze results to ensure it works correctly.`,
        steps: [
          'Use "Configure & Run" for advanced options',
          'Enable "Headed Mode" to watch execution in real-time',
          'Check failed step screenshots for debugging',
          'Use the AI Self-Healing feature to auto-fix broken selectors'
        ]
      }
    ],
    tips: [
      'Include explicit wait times for dynamic content',
      'Use unique element identifiers when possible (IDs, data-testid)',
      'Test on multiple browsers to ensure cross-browser compatibility'
    ]
  },
  'understanding-dashboard': {
    title: 'Understanding the Dashboard',
    duration: '8 min',
    level: 'Beginner',
    sections: [
      {
        title: '1. Dashboard Overview',
        content: `The dashboard provides a quick view of your testing activity and key metrics.`,
        steps: [
          'Total Tests - Number of tests across all modules',
          'Pass Rate - Overall success percentage',
          'Recent Executions - Latest test run results',
          'Quick Actions - Fast access to common tasks'
        ]
      },
      {
        title: '2. Navigation Sidebar',
        content: `Use the left sidebar to navigate between different testing modules.`,
        steps: [
          'Dashboard - Overview and quick stats',
          'Flow Testing - UI automation tests',
          'API Testing - REST API tests and chains',
          'Sprint QA - Requirements and test cases',
          'Test Suites - Grouped test collections',
          'Integrations - CI/CD and tool connections',
          'Settings - Account and workspace configuration'
        ]
      },
      {
        title: '3. Project Switching',
        content: `Organize your tests by project for better management.`,
        steps: [
          'Click the project dropdown in the header',
          'Select an existing project or create new',
          'Each project has isolated tests and settings',
          'Use project prefixes for easy identification (e.g., PROJ-001)'
        ]
      },
      {
        title: '4. User Settings',
        content: `Customize your experience and manage your account.`,
        steps: [
          'Click your avatar → Settings',
          'Update profile information and password',
          'Manage notification preferences',
          'View billing and subscription details'
        ]
      }
    ],
    tips: [
      'Use keyboard shortcuts for faster navigation',
      'Pin frequently used projects to the sidebar',
      'Enable dark mode in Settings for reduced eye strain'
    ]
  },
  'building-test-flows': {
    title: 'Building Test Flows',
    duration: '15 min',
    level: 'Intermediate',
    sections: [
      {
        title: '1. What are Test Flows?',
        content: `Test Flows are visual representations of user journeys through your application. Each flow consists of sequential steps that simulate real user interactions.`,
        steps: [
          'Navigate actions - go to URLs, click links',
          'Input actions - type text, select options, upload files',
          'Validation actions - assert text, check visibility',
          'Wait actions - pause, wait for elements'
        ]
      },
      {
        title: '2. Creating a Flow',
        content: `Build comprehensive test scenarios using the visual flow builder.`,
        steps: [
          'Go to Flow Testing → Tests → Create Test',
          'Enter a descriptive test name',
          'Add steps using the step builder or AI generation',
          'Configure step properties (selectors, values, timeouts)'
        ]
      },
      {
        title: '3. Available Step Types',
        content: `Lexa.run supports all common browser interactions:`,
        code: `Step Types:
• navigate    - Go to a URL
• click       - Click an element
• type        - Enter text into a field
• select      - Choose dropdown option
• check       - Check/uncheck checkbox
• hover       - Hover over element
• scroll      - Scroll to element/position
• wait        - Wait for time/element
• screenshot  - Capture page state
• assert      - Verify conditions`,
        steps: []
      },
      {
        title: '4. Using Selectors',
        content: `Selectors identify which element to interact with. Use robust selectors for reliable tests.`,
        code: `Selector Priority (Best to Worst):
1. data-testid="login-button"   // Best - stable
2. #login-btn                    // Good - unique ID
3. .btn-primary                  // OK - class name
4. button:has-text("Login")     // OK - text content
5. //button[@type='submit']     // Avoid - fragile XPath`,
        steps: [
          'Prefer data-testid attributes for stability',
          'Use unique IDs when available',
          'Avoid position-based selectors (nth-child)',
          'Test selectors in browser DevTools first'
        ]
      },
      {
        title: '5. Test Segments (Reusable Components)',
        content: `Create reusable test segments for common workflows like login.`,
        steps: [
          'Go to Flow Testing → Segments',
          'Create a segment with common steps (e.g., login flow)',
          'Insert segments into any test',
          'Update segment once to update all tests using it'
        ]
      }
    ],
    tips: [
      'Use segments for login/logout flows to avoid repetition',
      'Add screenshot steps at key points for debugging',
      'Set appropriate timeouts for slow-loading pages'
    ]
  },
  'recording-browser-actions': {
    title: 'Recording Browser Actions',
    duration: '12 min',
    level: 'Intermediate',
    sections: [
      {
        title: '1. Live Recording Overview',
        content: `The Live Recorder lets you interact with a real browser and automatically captures your actions as test steps.`,
        steps: [
          'Actions are recorded in real-time',
          'Supports click, type, select, and navigation',
          'AI enhances recordings with better selectors',
          'Edit and refine recordings after capture'
        ]
      },
      {
        title: '2. Starting a Recording Session',
        content: `Launch the live recorder to begin capturing your actions.`,
        steps: [
          'Go to Flow Testing → Tests → Create Test',
          'Click "Record with Live Session"',
          'Enter the starting URL for your test',
          'Wait for the browser to load'
        ]
      },
      {
        title: '3. Recording Best Practices',
        content: `Follow these tips for high-quality recordings:`,
        steps: [
          'Move slowly and deliberately - rushed actions may not capture',
          'Wait for pages to fully load before clicking',
          'Use clear, visible elements for clicks',
          'Avoid scrolling too fast'
        ]
      },
      {
        title: '4. Finishing and Editing',
        content: `After recording, review and enhance your captured test.`,
        steps: [
          'Click "Stop Recording" when done',
          'Review each captured step',
          'Delete unnecessary steps (accidental clicks)',
          'Add assertions and wait conditions',
          'Rename steps for clarity'
        ]
      },
      {
        title: '5. Adding Manual Screenshots',
        content: `Insert screenshot steps to capture visual evidence.`,
        steps: [
          'Click "Add Step" → Screenshot',
          'Choose full page or element screenshot',
          'Screenshots are saved with execution results',
          'Use for visual regression testing'
        ]
      }
    ],
    tips: [
      'Record in a clean browser state (cleared cookies)',
      'Use consistent test data for repeatable results',
      'Add explicit waits after page transitions'
    ]
  },
  'using-variables-data': {
    title: 'Using Variables & Data',
    duration: '10 min',
    level: 'Intermediate',
    sections: [
      {
        title: '1. Environment Variables',
        content: `Store configuration values that change between environments (dev, staging, prod).`,
        code: `Example Environment Variables:
BASE_URL=https://staging.example.com
API_KEY=sk-test-12345
USERNAME=testuser
PASSWORD=secure123`,
        steps: [
          'Go to Flow Testing → Environments',
          'Create environment (e.g., "Staging")',
          'Add key-value pairs for variables',
          'Reference with {{VARIABLE_NAME}} syntax'
        ]
      },
      {
        title: '2. Using Variables in Tests',
        content: `Reference environment variables in your test steps using double curly braces.`,
        code: `Step Configuration:
URL: {{BASE_URL}}/login
Username: {{USERNAME}}
Password: {{PASSWORD}}

The values are substituted at runtime based on 
the selected environment.`,
        steps: []
      },
      {
        title: '3. Dynamic Data Extraction',
        content: `Extract values from page content to use in subsequent steps.`,
        steps: [
          'Use "Extract Text" action to capture dynamic values',
          'Store extracted values in test variables',
          'Reference extracted values: {{extracted_order_id}}',
          'Chain extractions across multiple steps'
        ]
      },
      {
        title: '4. Data-Driven Testing',
        content: `Run the same test with multiple data sets.`,
        steps: [
          'Create a CSV file with test data columns',
          'Upload data file to your test',
          'Map columns to test variables',
          'Test runs once per row in the data file'
        ]
      }
    ],
    tips: [
      'Never hardcode sensitive data - use environment variables',
      'Create separate environments for each deployment stage',
      'Use descriptive variable names for clarity'
    ]
  },
  'api-test-basics': {
    title: 'API Test Basics',
    duration: '10 min',
    level: 'Beginner',
    sections: [
      {
        title: '1. What is API Testing?',
        content: `API testing validates your application's backend services directly, without a browser. It's faster and more reliable than UI testing for backend logic.`,
        steps: [
          'Test REST endpoints (GET, POST, PUT, DELETE)',
          'Validate response status codes and body content',
          'Check response times and performance',
          'Verify authentication and authorization'
        ]
      },
      {
        title: '2. Creating Your First API Test',
        content: `Set up a basic API test to verify an endpoint works correctly.`,
        steps: [
          'Go to API Testing → Create Test',
          'Enter the API endpoint URL',
          'Select HTTP method (GET, POST, etc.)',
          'Add headers (Authorization, Content-Type)',
          'Add request body for POST/PUT requests',
          'Click "Run Test" to execute'
        ]
      },
      {
        title: '3. Request Configuration',
        content: `Configure your API request with the necessary details.`,
        code: `Example POST Request:
URL: https://api.example.com/users
Method: POST
Headers:
  Content-Type: application/json
  Authorization: Bearer {{API_TOKEN}}
Body:
{
  "name": "John Doe",
  "email": "john@example.com"
}`,
        steps: []
      },
      {
        title: '4. Adding Assertions',
        content: `Validate that the API response meets your expectations.`,
        code: `Common Assertions:
• Status Code equals 200
• Response time less than 2000ms
• Body contains "success": true
• Header Content-Type equals application/json`,
        steps: [
          'Click "Add Assertion" in the test editor',
          'Select assertion type (Status, Body, Header, Time)',
          'Configure expected values',
          'Multiple assertions run in order'
        ]
      },
      {
        title: '5. Viewing Results',
        content: `Analyze test execution results and debug failures.`,
        steps: [
          'Check status code and response time',
          'View full response body (JSON formatted)',
          'See assertion pass/fail status',
          'Download response for offline analysis'
        ]
      }
    ],
    tips: [
      'Always validate status codes - don\'t assume 200',
      'Test error scenarios (404, 401, 500)',
      'Use environment variables for API keys'
    ]
  },
  'data-chaining-extraction': {
    title: 'Data Chaining & Extraction',
    duration: '15 min',
    level: 'Intermediate',
    sections: [
      {
        title: '1. What is Data Chaining?',
        content: `Data chaining lets you extract values from one API response and use them in subsequent requests. This enables testing complex multi-step workflows.`,
        code: `Example Flow:
1. POST /auth/login → Extract "token"
2. GET /users/me (with token) → Extract "user_id"  
3. POST /orders (with user_id) → Extract "order_id"
4. GET /orders/{order_id} → Verify order details`,
        steps: []
      },
      {
        title: '2. Extracting Data',
        content: `Configure extractions to capture values from API responses.`,
        steps: [
          'Open the "Extract" tab in your API test',
          'Click "Add Extraction"',
          'Enter a variable name (e.g., "auth_token")',
          'Select source: Response Body, Headers, Status Code',
          'Enter the path using JSONPath or dot notation'
        ]
      },
      {
        title: '3. JSONPath Syntax',
        content: `Use JSONPath to extract values from JSON responses.`,
        code: `JSONPath Examples:
$.token              → Root level "token" field
$.data.user.id       → Nested user ID
$.items[0].name      → First item's name
$.items[*].id        → All item IDs (array)
$.users[?(@.active)].email → Emails of active users`,
        steps: []
      },
      {
        title: '4. Using Extracted Variables',
        content: `Reference extracted values in subsequent API tests.`,
        code: `Using Variables:
URL: {{BASE_URL}}/users/{{user_id}}/orders
Headers:
  Authorization: Bearer {{auth_token}}
Body:
{
  "customer_id": "{{extracted_customer_id}}"
}`,
        steps: [
          'Use {{variable_name}} syntax anywhere in requests',
          'Variables work in URLs, headers, and body',
          'Extracted values persist across chain steps',
          'Set fallback values for optional extractions'
        ]
      },
      {
        title: '5. Building API Chains',
        content: `Link multiple API tests into automated workflows.`,
        steps: [
          'Go to API Testing → Chains → Create Chain',
          'Add tests in execution order',
          'Configure stop-on-fail behavior',
          'Set delays between steps if needed',
          'Run the entire chain with one click'
        ]
      }
    ],
    tips: [
      'Use descriptive variable names: "auth_token" not "t1"',
      'Add fallback values for optional fields',
      'Test extraction paths in isolation first'
    ]
  },
  'building-api-workflows': {
    title: 'Building API Workflows',
    duration: '20 min',
    level: 'Advanced',
    sections: [
      {
        title: '1. API Test Chains',
        content: `API Test Chains let you orchestrate multiple API tests into complex workflows with data flowing between steps.`,
        steps: [
          'Create individual API tests for each endpoint',
          'Configure extractions to capture needed values',
          'Build a chain linking tests in sequence',
          'Run the entire workflow with one click'
        ]
      },
      {
        title: '2. Creating a Chain',
        content: `Set up a new API test chain with multiple steps.`,
        steps: [
          'Go to API Testing → Chains',
          'Click "Create Chain"',
          'Enter chain name and description',
          'Add tests from your existing API tests',
          'Arrange steps in execution order'
        ]
      },
      {
        title: '3. Configuring Chain Steps',
        content: `Fine-tune each step's behavior in the workflow.`,
        code: `Step Configuration:
• Order: Execution sequence (1, 2, 3...)
• Stop on Fail: Halt chain if step fails
• Condition: Run only if expression is true
• Delay: Wait time before this step (ms)
• Description: Document step purpose`,
        steps: []
      },
      {
        title: '4. Conditional Execution',
        content: `Add conditions to control which steps run based on previous results.`,
        code: `Condition Examples:
• {{status_code}} == 200
• {{user_type}} == "admin"
• {{items_count}} > 0
• {{response_time}} < 1000`,
        steps: [
          'Click "Add Condition" on a chain step',
          'Enter a JavaScript expression',
          'Use extracted variables in conditions',
          'Step skips if condition is false'
        ]
      },
      {
        title: '5. Chain Execution & Results',
        content: `Run chains and analyze comprehensive results.`,
        steps: [
          'Click "Run Chain" to execute all steps',
          'View real-time progress as steps complete',
          'See individual step results and extractions',
          'Download full execution report',
          'Re-run failed chains with same data'
        ]
      },
      {
        title: '6. Example: E-Commerce Checkout Flow',
        content: `A complete example chain testing an e-commerce checkout.`,
        code: `Checkout Test Chain:
1. POST /auth/login
   Extract: auth_token, user_id
   
2. GET /products
   Extract: product_id, product_price
   
3. POST /cart/add
   Body: { product_id, quantity: 1 }
   Extract: cart_id
   
4. POST /checkout
   Body: { cart_id, payment_method: "card" }
   Extract: order_id
   
5. GET /orders/{order_id}
   Assert: status == "confirmed"`,
        steps: []
      }
    ],
    tips: [
      'Start chains with authentication steps',
      'Use stop-on-fail for critical steps',
      'Add delays for rate-limited APIs',
      'Document each step\'s purpose'
    ]
  },
  'managing-requirements': {
    title: 'Managing Requirements',
    duration: '12 min',
    level: 'Beginner',
    sections: [
      {
        title: '1. Requirements Overview',
        content: `Requirements are the foundation of your QA process. They define what your application should do and serve as the basis for test cases.`,
        steps: [
          'Each requirement has a unique ID (e.g., REQ-001)',
          'Requirements can have acceptance criteria',
          'Link test scenarios to requirements',
          'Track requirement coverage'
        ]
      },
      {
        title: '2. Creating Requirements',
        content: `Add requirements to document what needs to be tested.`,
        steps: [
          'Go to Sprint QA → Requirements',
          'Click "Create Requirement"',
          'Enter title and description',
          'Set priority (Critical, High, Medium, Low)',
          'Assign to team member (optional)',
          'Add tags for organization'
        ]
      },
      {
        title: '3. Acceptance Criteria',
        content: `Define specific conditions that must be met for a requirement to be considered complete.`,
        code: `Example Requirement:
Title: User Login
Description: Users must be able to log in

Acceptance Criteria:
✓ User can enter email and password
✓ Error shown for invalid credentials
✓ Successful login redirects to dashboard
✓ "Remember me" option persists session
✓ Password can be reset via email`,
        steps: []
      },
      {
        title: '4. Organizing Requirements',
        content: `Keep requirements organized for easy management.`,
        steps: [
          'Use tags to categorize (feature, bugfix, enhancement)',
          'Set status: Draft, Active, Completed, Archived',
          'Filter by priority, status, or assignee',
          'Link related requirements together'
        ]
      },
      {
        title: '5. AI-Powered Generation',
        content: `Let AI help generate requirements from descriptions.`,
        steps: [
          'Click "Generate with AI"',
          'Describe the feature in plain English',
          'AI creates requirement with acceptance criteria',
          'Review and edit as needed',
          'Save to your project'
        ]
      }
    ],
    tips: [
      'Write requirements from the user\'s perspective',
      'Keep acceptance criteria specific and measurable',
      'Review requirements with stakeholders before testing'
    ]
  },
  'test-case-management': {
    title: 'Test Case Management',
    duration: '15 min',
    level: 'Intermediate',
    sections: [
      {
        title: '1. Test Cases Overview',
        content: `Test cases document the specific steps to verify a requirement works correctly. Each test case has detailed steps, expected results, and evidence.`,
        steps: [
          'Linked to requirements and test scenarios',
          'Contains step-by-step instructions',
          'Defines expected results for each step',
          'Supports evidence upload (screenshots, files)'
        ]
      },
      {
        title: '2. Creating Test Cases',
        content: `Build comprehensive test cases with detailed steps.`,
        steps: [
          'Go to Sprint QA → Test Cases',
          'Click "Create Test Case"',
          'Enter title and description',
          'Link to a requirement or test scenario',
          'Add test steps with expected results'
        ]
      },
      {
        title: '3. Test Step Structure',
        content: `Each test step should be clear and actionable.`,
        code: `Test Step Format:
Step #  | Action            | Test Data        | Expected Result
--------|-------------------|------------------|------------------
1       | Navigate to URL   | /login           | Login page loads
2       | Enter username    | testuser@test.com| Field populated
3       | Enter password    | Test123!         | Password masked
4       | Click Login       | -                | Redirects to dash
5       | Verify welcome    | -                | "Welcome" shown`,
        steps: []
      },
      {
        title: '4. Adding Evidence',
        content: `Upload screenshots and files as evidence for test steps.`,
        steps: [
          'Click the evidence icon on any step',
          'Upload images (PNG, JPG, WebP, GIF)',
          'Add descriptions to evidence',
          'Evidence is auto-compressed for storage',
          'View evidence in lightbox gallery'
        ]
      },
      {
        title: '5. Test Case Status',
        content: `Track the lifecycle of each test case.`,
        steps: [
          'Draft - Still being written',
          'Ready - Ready for execution',
          'In Progress - Currently being executed',
          'Passed - All steps passed',
          'Failed - One or more steps failed',
          'Blocked - Cannot execute (dependency issue)'
        ]
      }
    ],
    tips: [
      'Write steps that anyone can follow',
      'Include exact test data values',
      'Add screenshots for complex UI steps',
      'Review test cases for completeness before execution'
    ]
  },
  'running-test-executions': {
    title: 'Running Test Executions',
    duration: '10 min',
    level: 'Beginner',
    sections: [
      {
        title: '1. What is a Test Execution?',
        content: `A test execution is a single run of a test case. It records the actual results for each step and captures evidence of the test outcome.`,
        steps: [
          'Each execution has a unique ID',
          'Records pass/fail for each step',
          'Captures timestamps and duration',
          'Stores uploaded evidence'
        ]
      },
      {
        title: '2. Starting an Execution',
        content: `Run a test case and record your results.`,
        steps: [
          'Open a test case in Sprint QA',
          'Click "Run Test" button',
          'Execution sheet opens with all steps',
          'Work through each step sequentially'
        ]
      },
      {
        title: '3. Recording Step Results',
        content: `Mark each step as passed, failed, or blocked.`,
        steps: [
          'Perform the action described in the step',
          'Compare actual result to expected result',
          'Click Pass ✓ if results match',
          'Click Fail ✗ if results differ',
          'Click Block ⊘ if step cannot be executed',
          'Add comments for failed/blocked steps'
        ]
      },
      {
        title: '4. Uploading Evidence',
        content: `Capture proof of your test results.`,
        steps: [
          'Click the camera icon during execution',
          'Take a screenshot of the current state',
          'Upload directly from your device',
          'Evidence is linked to the specific step'
        ]
      },
      {
        title: '5. Completing the Execution',
        content: `Finish the test run and review results.`,
        steps: [
          'Mark all steps with results',
          'Add overall execution notes',
          'Click "Complete Execution"',
          'View execution summary',
          'Share results with team'
        ]
      },
      {
        title: '6. Execution History',
        content: `Track all executions of a test case over time.`,
        steps: [
          'View past executions in the History tab',
          'Compare results across executions',
          'Identify flaky tests (inconsistent results)',
          'Generate trend reports'
        ]
      }
    ],
    tips: [
      'Always add evidence for failed steps',
      'Write clear comments explaining failures',
      'Re-run tests after bug fixes to verify'
    ]
  },
  'github-actions-setup': {
    title: 'GitHub Actions Setup',
    duration: '15 min',
    level: 'Intermediate',
    sections: [
      {
        title: '1. Overview',
        content: `Integrate Lexa.run tests into your GitHub Actions CI/CD pipeline to run tests automatically on every push or pull request.`,
        steps: [
          'Generate API key from Lexa.run',
          'Add workflow file to your repository',
          'Configure test triggers',
          'View results in GitHub Actions'
        ]
      },
      {
        title: '2. Generate API Key',
        content: `Create an API key to authenticate your CI/CD pipeline.`,
        steps: [
          'Go to Settings → Integrations → CI/CD',
          'Click "Generate API Key"',
          'Copy the key (shown only once)',
          'Add key as GitHub secret: LEXA_API_KEY'
        ]
      },
      {
        title: '3. Create Workflow File',
        content: `Add a GitHub Actions workflow to run Lexa tests.`,
        code: `# .github/workflows/lexa-tests.yml
name: Lexa.run Tests

on:
  push:
    branches: [main, develop]
  pull_request:
    branches: [main]

jobs:
  test:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
      
      - name: Run Lexa Tests
        env:
          LEXA_API_KEY: \${{ secrets.LEXA_API_KEY }}
        run: |
          curl -X POST "https://api.lexa.run/v1/suites/run" \\
            -H "Authorization: Bearer $LEXA_API_KEY" \\
            -H "Content-Type: application/json" \\
            -d '{"suite_id": "your-suite-id"}'`,
        steps: []
      },
      {
        title: '4. Configuring Triggers',
        content: `Control when tests run in your pipeline.`,
        code: `Trigger Options:
• on: push          # Run on every push
• on: pull_request  # Run on PRs
• on: schedule      # Run on cron schedule
• on: workflow_dispatch  # Manual trigger

Example scheduled run:
on:
  schedule:
    - cron: '0 6 * * *'  # Daily at 6 AM UTC`,
        steps: []
      },
      {
        title: '5. Viewing Results',
        content: `Check test results directly in GitHub.`,
        steps: [
          'Go to Actions tab in your repository',
          'Click on the workflow run',
          'View test output in logs',
          'See detailed results in Lexa.run dashboard'
        ]
      }
    ],
    tips: [
      'Use branch protection to require passing tests',
      'Cache dependencies to speed up runs',
      'Set appropriate timeouts for long tests'
    ]
  },
  'jenkins-integration': {
    title: 'Jenkins Integration',
    duration: '15 min',
    level: 'Advanced',
    sections: [
      {
        title: '1. Overview',
        content: `Integrate Lexa.run with Jenkins to run tests as part of your existing CI/CD pipelines.`,
        steps: [
          'Generate API key from Lexa.run',
          'Configure Jenkins credentials',
          'Add Lexa test step to pipeline',
          'View results in Jenkins'
        ]
      },
      {
        title: '2. Jenkins Credentials Setup',
        content: `Store your Lexa API key securely in Jenkins.`,
        steps: [
          'Go to Jenkins → Manage Jenkins → Credentials',
          'Add new Secret Text credential',
          'Enter your Lexa API key',
          'Set ID: "lexa-api-key"'
        ]
      },
      {
        title: '3. Pipeline Configuration',
        content: `Add Lexa tests to your Jenkinsfile.`,
        code: `// Jenkinsfile
pipeline {
    agent any
    
    environment {
        LEXA_API_KEY = credentials('lexa-api-key')
    }
    
    stages {
        stage('Build') {
            steps {
                sh 'npm install && npm run build'
            }
        }
        
        stage('Lexa Tests') {
            steps {
                sh '''
                    curl -X POST "https://api.lexa.run/v1/suites/run" \\
                        -H "Authorization: Bearer $LEXA_API_KEY" \\
                        -H "Content-Type: application/json" \\
                        -d '{"suite_id": "your-suite-id"}' \\
                        -o result.json
                    
                    # Check for failures
                    if grep -q '"status":"failed"' result.json; then
                        exit 1
                    fi
                '''
            }
        }
    }
    
    post {
        always {
            archiveArtifacts artifacts: 'result.json'
        }
    }
}`,
        steps: []
      },
      {
        title: '4. Parallel Test Execution',
        content: `Run multiple test suites in parallel for faster feedback.`,
        code: `stage('Lexa Tests') {
    parallel {
        stage('UI Tests') {
            steps {
                sh 'curl ... -d \'{"suite_id": "ui-suite"}\''
            }
        }
        stage('API Tests') {
            steps {
                sh 'curl ... -d \'{"suite_id": "api-suite"}\''
            }
        }
    }
}`,
        steps: []
      },
      {
        title: '5. Results & Notifications',
        content: `Configure notifications for test results.`,
        steps: [
          'Use Jenkins email notification plugin',
          'Send Slack messages on failure',
          'Update GitHub commit status',
          'Archive test artifacts for review'
        ]
      }
    ],
    tips: [
      'Use Jenkins shared libraries for reusable code',
      'Set up separate stages for different test types',
      'Configure timeouts to prevent hanging jobs'
    ]
  },
  'scheduled-test-runs': {
    title: 'Scheduled Test Runs',
    duration: '8 min',
    level: 'Beginner',
    sections: [
      {
        title: '1. Why Schedule Tests?',
        content: `Automated scheduled tests catch issues proactively, even when no one is actively testing.`,
        steps: [
          'Run regression tests overnight',
          'Monitor production with smoke tests',
          'Check integrations after deployments',
          'Verify third-party APIs are working'
        ]
      },
      {
        title: '2. Creating a Schedule',
        content: `Set up automated test runs on a recurring schedule.`,
        steps: [
          'Go to Test Suites → Select a suite',
          'Click "Schedule" tab',
          'Click "Add Schedule"',
          'Configure frequency and time',
          'Enable/disable as needed'
        ]
      },
      {
        title: '3. Schedule Options',
        content: `Choose how often tests should run.`,
        code: `Schedule Frequencies:
• Hourly    - Every hour
• Daily     - Once per day at specific time
• Weekly    - Specific days of the week
• Monthly   - Specific day each month
• Custom    - Cron expression for advanced

Example: Daily at 6 AM UTC
"Run every day at 6:00 AM UTC"

Example: Weekdays only
"Run Mon-Fri at 9:00 AM UTC"`,
        steps: []
      },
      {
        title: '4. Notifications',
        content: `Get alerted when scheduled tests complete.`,
        steps: [
          'Configure email notifications',
          'Set up Slack webhook alerts',
          'Choose: All results or Failures only',
          'Include test summary in notification'
        ]
      },
      {
        title: '5. Managing Schedules',
        content: `View and manage all your scheduled tests.`,
        steps: [
          'View all schedules in Settings → Schedules',
          'See next run time for each schedule',
          'Pause schedules during maintenance',
          'Delete schedules no longer needed'
        ]
      }
    ],
    tips: [
      'Run production smoke tests every hour',
      'Schedule heavy regression tests overnight',
      'Set up failure alerts to catch issues early'
    ]
  }
};

const Guides = ({ onBack, onLogin, onRegister, navigateTo }) => {
  const [selectedGuide, setSelectedGuide] = useState(null);

  const guides = [
    {
      category: 'Getting Started',
      icon: Zap,
      items: [
        { id: 'quick-start', title: 'Quick Start Guide', description: 'Get up and running with Lexa.run in under 5 minutes', duration: '5 min', level: 'Beginner' },
        { id: 'creating-first-test', title: 'Creating Your First Test', description: 'Learn how to create and run your first AI-generated test', duration: '10 min', level: 'Beginner' },
        { id: 'understanding-dashboard', title: 'Understanding the Dashboard', description: 'Navigate the Lexa.run dashboard and key features', duration: '8 min', level: 'Beginner' }
      ]
    },
    {
      category: 'Flow Testing',
      icon: Workflow,
      items: [
        { id: 'building-test-flows', title: 'Building Test Flows', description: 'Create complex test scenarios with the visual flow builder', duration: '15 min', level: 'Intermediate' },
        { id: 'recording-browser-actions', title: 'Recording Browser Actions', description: 'Use the live recorder to capture test steps automatically', duration: '12 min', level: 'Intermediate' },
        { id: 'using-variables-data', title: 'Using Variables & Data', description: 'Parameterize your tests with dynamic data', duration: '10 min', level: 'Intermediate' }
      ]
    },
    {
      category: 'API Testing',
      icon: TestTube2,
      items: [
        { id: 'api-test-basics', title: 'API Test Basics', description: 'Create and execute REST API tests', duration: '10 min', level: 'Beginner' },
        { id: 'data-chaining-extraction', title: 'Data Chaining & Extraction', description: 'Extract data from responses and use in subsequent tests', duration: '15 min', level: 'Intermediate' },
        { id: 'building-api-workflows', title: 'Building API Workflows', description: 'Chain multiple API tests into automated workflows', duration: '20 min', level: 'Advanced' }
      ]
    },
    {
      category: 'Sprint QA',
      icon: BookOpen,
      items: [
        { id: 'managing-requirements', title: 'Managing Requirements', description: 'Create and organize requirements with acceptance criteria', duration: '12 min', level: 'Beginner' },
        { id: 'test-case-management', title: 'Test Case Management', description: 'Build comprehensive test cases with steps and evidence', duration: '15 min', level: 'Intermediate' },
        { id: 'running-test-executions', title: 'Running Test Executions', description: 'Execute test cases and track results', duration: '10 min', level: 'Beginner' }
      ]
    },
    {
      category: 'CI/CD Integration',
      icon: Code2,
      items: [
        { id: 'github-actions-setup', title: 'GitHub Actions Setup', description: 'Run Lexa tests on every push with GitHub Actions', duration: '15 min', level: 'Intermediate' },
        { id: 'jenkins-integration', title: 'Jenkins Integration', description: 'Integrate Lexa.run with your Jenkins pipelines', duration: '15 min', level: 'Advanced' },
        { id: 'scheduled-test-runs', title: 'Scheduled Test Runs', description: 'Set up automated test schedules', duration: '8 min', level: 'Beginner' }
      ]
    }
  ];

  const getLevelBadge = (level) => {
    switch (level) {
      case 'Beginner':
        return <Badge className="bg-green-100 text-green-700 text-xs">{level}</Badge>;
      case 'Intermediate':
        return <Badge className="bg-blue-100 text-blue-700 text-xs">{level}</Badge>;
      case 'Advanced':
        return <Badge className="bg-purple-100 text-purple-700 text-xs">{level}</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700 text-xs">{level}</Badge>;
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Render detailed guide content
  if (selectedGuide && guideContent[selectedGuide]) {
    const guide = guideContent[selectedGuide];
    
    return (
      <SharedPageLayout
        title={`${guide.title} | Lexa.run Guides`}
        description={`Learn ${guide.title.toLowerCase()} with this step-by-step tutorial.`}
        onBack={onBack}
        onLogin={onLogin}
        onRegister={onRegister}
        navigateTo={navigateTo}
      >
        <div className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Back button */}
            <Button
              variant="ghost"
              className="mb-6 text-gray-600 hover:text-gray-900"
              onClick={() => setSelectedGuide(null)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Guides
            </Button>

            {/* Guide header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                {getLevelBadge(guide.level)}
                <div className="flex items-center text-sm text-gray-500">
                  <Clock className="w-4 h-4 mr-1" />
                  {guide.duration} read
                </div>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {guide.title}
              </h1>
            </div>

            {/* Guide sections */}
            <div className="space-y-10">
              {guide.sections.map((section, idx) => (
                <div key={idx} className="border-b border-gray-100 pb-8 last:border-0">
                  <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="w-8 h-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-bold mr-3">
                      {idx + 1}
                    </span>
                    {section.title}
                  </h2>
                  
                  {section.content && (
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      {section.content}
                    </p>
                  )}

                  {section.code && (
                    <div className="relative mb-4">
                      <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm">
                        <code>{section.code}</code>
                      </pre>
                      <button
                        onClick={() => copyToClipboard(section.code)}
                        className="absolute top-2 right-2 p-2 text-gray-400 hover:text-white transition-colors"
                        title="Copy to clipboard"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {section.steps && section.steps.length > 0 && (
                    <ul className="space-y-2">
                      {section.steps.map((step, stepIdx) => (
                        <li key={stepIdx} className="flex items-start gap-3 text-gray-700">
                          <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span>{step}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>

            {/* Tips section */}
            {guide.tips && guide.tips.length > 0 && (
              <div className="mt-10 p-6 bg-amber-50 border border-amber-200 rounded-xl">
                <h3 className="flex items-center text-lg font-semibold text-amber-800 mb-4">
                  <Lightbulb className="w-5 h-5 mr-2" />
                  Pro Tips
                </h3>
                <ul className="space-y-2">
                  {guide.tips.map((tip, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-amber-900">
                      <span className="text-amber-500">•</span>
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Navigation */}
            <div className="mt-12 flex justify-between items-center pt-8 border-t border-gray-200">
              <Button
                variant="outline"
                onClick={() => setSelectedGuide(null)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                All Guides
              </Button>
              <Button
                onClick={onRegister}
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white"
              >
                Try Lexa.run Free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </SharedPageLayout>
    );
  }

  // Render guides list
  return (
    <SharedPageLayout
      title="Guides & Tutorials | Lexa.run"
      description="Learn how to use Lexa.run with our comprehensive guides and tutorials. From getting started to advanced CI/CD integration."
      onBack={onBack}
      onLogin={onLogin}
      onRegister={onRegister}
      navigateTo={navigateTo}
    >
      {/* Hero Section */}
      <section className="pt-16 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge variant="outline" className="mb-4 text-purple-600 border-purple-200">
            Learning Resources
          </Badge>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-600">
              Guides & Tutorials
            </span>
          </h1>
          <p className="text-xl text-gray-600">
            Step-by-step guides to help you master test automation with Lexa.run.
          </p>
        </div>
      </section>

      {/* Guides Grid */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {guides.map((category, categoryIndex) => (
              <div key={categoryIndex}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <category.icon className="w-5 h-5 text-purple-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">{category.category}</h2>
                </div>
                
                <div className="grid md:grid-cols-3 gap-6">
                  {category.items.map((guide, guideIndex) => (
                    <Card 
                      key={guideIndex} 
                      className="p-6 border-gray-200/50 hover:shadow-lg transition-all hover:-translate-y-1 cursor-pointer group"
                      onClick={() => setSelectedGuide(guide.id)}
                      data-testid={`guide-card-${guide.id}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        {getLevelBadge(guide.level)}
                        <div className="flex items-center text-sm text-gray-500">
                          <Clock className="w-4 h-4 mr-1" />
                          {guide.duration}
                        </div>
                      </div>
                      
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-purple-700 transition-colors">
                        {guide.title}
                      </h3>
                      
                      <p className="text-gray-600 text-sm mb-4">
                        {guide.description}
                      </p>
                      
                      <div className="flex items-center text-purple-600 font-medium text-sm group-hover:gap-2 gap-1 transition-all">
                        Read Guide
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 mb-6">
            <Play className="w-8 h-8 text-purple-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Prefer Video Tutorials?
          </h2>
          <p className="text-gray-600 mb-6">
            Watch our video walkthroughs for hands-on learning.
          </p>
          <Button 
            variant="outline"
            className="rounded-full"
            onClick={() => window.open('https://www.youtube.com/@lexarun', '_blank')}
          >
            <Play className="w-5 h-5 mr-2" />
            Watch on YouTube
          </Button>
        </div>
      </section>
    </SharedPageLayout>
  );
};

export default Guides;
